/**
 * The route tree. Defined at module scope so the router is created once,
 * never per render.
 *
 * Route map:
 *   /                → LibraryView (EAGER — the boot surface)
 *   /notes           → LibraryView in notes context (GlobalNotesView itself
 *                      is lazy inside LibraryView; the view context is route
 *                      state, not a synced preference)
 *   /search          → LibraryView in search context (the global semantic
 *                      search view, lazy inside LibraryView)
 *   /drive           → LibraryView in Drive context (the Drive shelf, lazy
 *                      inside LibraryView; the import dialog's "Google Drive"
 *                      choice navigates here instead of opening a modal)
 *   /read/:id        → ReaderShell, React.lazy (pulls epubjs out of the
 *                      entry chunk — asserted by check 4 of
 *                      scripts/check-worker-chunk.mjs)
 *   /settings/:tab?  → SettingsShell over the library (URL-addressable
 *                      overlay; registry-driven, see ./settings/registry.ts).
 *                      Closing = history back, so hardware/browser back
 *                      "closes the dialog" with no dedicated guard.
 *   /read/:id/settings/:tab? → the SAME SettingsShell, but nested under the
 *                      reader so opening settings from a book does NOT unmount
 *                      ReaderShell (the book stays live behind the overlay and
 *                      the "This Book" lexicon scope stays reachable).
 */
import { Suspense, lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { RootLayout } from '../layouts/RootLayout';
import { LibraryView } from '@components/library/LibraryView';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { importWithChunkReload } from '@lib/chunkReload';

// Route chunks go through importWithChunkReload: a dynamic import aborted
// mid-flight (e.g. by the workspace switch's back-to-back reloads) is cached
// as rejected for the document's lifetime — without the reload-once recovery
// the route stays permanently broken until the user refreshes by hand.
const loadReaderShell = importWithChunkReload(
  () => import('@components/reader/ReaderShell').then((m) => ({ default: m.ReaderShell })),
  'reader-shell',
);
const ReaderShellLazy = lazy(loadReaderShell);

// Warm the reader chunk shortly after the window load event: opening a book
// is the primary action from the boot surface, and the chunk fetch + parse
// (epubjs rides in it) is otherwise a serial slice of every first-open. The
// delay keeps the warmup out of boot's network/CPU window; the import cache
// makes the later route navigation a hit. Deliberately setTimeout, not
// requestIdleCallback — WebKit has no requestIdleCallback.
if (typeof window !== 'undefined') {
  const warmReaderChunk = () => {
    setTimeout(() => {
      loadReaderShell().catch(() => {
        // Prefetch is best-effort; the route's own load path handles errors.
      });
    }, 1500);
  };
  if (document.readyState === 'complete') {
    warmReaderChunk();
  } else {
    window.addEventListener('load', warmReaderChunk, { once: true });
  }
}
const SettingsShellLazy = lazy(
  importWithChunkReload(
    () => import('./settings/SettingsShell').then((m) => ({ default: m.SettingsShell })),
    'settings-shell',
  ),
);

/** Shared route-chunk fallback — the boot spinner, reused. */
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <div
        className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

// Note: We use ErrorBoundary components within routes to isolate failures.
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: (
      <ErrorBoundary>
        <div className="flex items-center justify-center h-screen bg-background text-foreground">
          <div className="p-8 border border-destructive/50 rounded-lg bg-destructive/10">
            <h2 className="text-xl font-bold mb-2">Application Error</h2>
            <p>A critical error occurred in the application shell.</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded">
              Reload Application
            </button>
          </div>
        </div>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary>
            <LibraryView />
          </ErrorBoundary>
        ),
      },
      {
        path: "notes",
        element: (
          <ErrorBoundary>
            <LibraryView context="notes" />
          </ErrorBoundary>
        ),
      },
      {
        path: "search",
        element: (
          <ErrorBoundary>
            <LibraryView context="search" />
          </ErrorBoundary>
        ),
      },
      {
        path: "drive",
        element: (
          <ErrorBoundary>
            <LibraryView context="drive" />
          </ErrorBoundary>
        ),
      },
      {
        path: "read/:id",
        element: (
          <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <ReaderShellLazy />
            </Suspense>
          </ErrorBoundary>
        ),
        children: [
          {
            // Settings opened FROM the reader nests here so ReaderShell stays
            // mounted (renders the overlay in its <Outlet/>). The book context
            // (useReaderUIStore.currentBookId) survives, so the Lexicon
            // Manager's "This Book" scope is reachable — unlike the top-level
            // /settings route, which renders over the library and unmounts the
            // reader. SettingsShell derives its tab base from the URL so tab
            // hops stay under /read/:id/settings instead of bouncing away.
            path: "settings/:tab?",
            element: (
              <ErrorBoundary>
                <Suspense fallback={null}>
                  <SettingsShellLazy />
                </Suspense>
              </ErrorBoundary>
            ),
          },
        ],
      },
      {
        // The settings overlay renders OVER the library (deep-link
        // acceptance: /settings/diagnostics cold-load = dialog on
        // Diagnostics over the library). In-app openers navigate here;
        // close = navigate back (or '/' on cold load).
        path: "settings/:tab?",
        element: (
          <ErrorBoundary>
            <LibraryView />
            <Suspense fallback={null}>
              <SettingsShellLazy />
            </Suspense>
          </ErrorBoundary>
        ),
      },
    ],
  },
]);
